// The Policy tab's lint control (arc 101 slice 06) —
// `POST /v1/repos/:o/:r/policy/validate` run against the ACTIVE snapshot, so a
// maintainer can ask "is my policy still accepted?" without pushing a commit to
// find out. The route existed with no caller at all.
//
// Why re-checking an already-active snapshot is not a no-op: the ingest
// (`apps/worker/src/consumers/policy-ingest.ts`) runs shape AND semantic
// validation before it activates, so an active snapshot was valid *the day it
// landed*. The semantic layer then tightens over time — `release.channels` and
// `review.depth` are both narrowed to what the platform can actually run
// (`SUPPORTED_RELEASE_CHANNELS` / `SUPPORTED_REVIEW_DEPTHS`) — and nothing
// re-reads a policy that already activated. Until the file changes, a repo can
// hold a snapshot today's rules would refuse, and no surface says so. This is
// that surface.
//
// The route takes YAML text; JSON is a subset of YAML, so the resolved snapshot
// posts as `JSON.stringify(snapshot)` — the same move the API's own `policyFor`
// makes (`parseYaml(JSON.stringify(raw))`).

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { apiFetch, errorReason } from './api';
import { repoApiBase } from './repo-detail';

/**
 * One refusal, in the wire's shape. The three producers differ:
 * `yaml` carries `line`/`column`, `shape` + `semantic` carry a `path`
 * (`packages/policy/src/{parse,validate}.ts`). All fields stay optional — the
 * client renders what arrived rather than asserting a union it can't enforce.
 */
export type PolicyLintError = {
  readonly kind?: string;
  readonly path?: readonly (string | number)[];
  readonly message?: string;
  readonly line?: number | null;
};

export type PolicyLintResult = {
  readonly ok: boolean;
  readonly errors?: readonly PolicyLintError[];
};

/** `["mcp_servers", 0, "transport"]` → `mcp_servers[0].transport`, the file's own spelling. */
export function formatPolicyPath(path: readonly (string | number)[]): string {
  return path.map((p, i) => (typeof p === 'number' ? `[${p}]` : i === 0 ? p : `.${p}`)).join('');
}

/** `pr.merge_decision — unknown value` / `line 12 — bad indentation`. */
export function formatLintError(e: PolicyLintError): string {
  const where =
    e.path && e.path.length > 0
      ? formatPolicyPath(e.path)
      : typeof e.line === 'number'
        ? t('repo_detail.policy_lint_line', { line: e.line })
        : (e.kind ?? '');
  const what = e.message ?? t('repo_detail.policy_lint_unknown');
  return where.length > 0 ? `${where} — ${what}` : what;
}

/** Ask the API to re-validate a snapshot. Throws `ApiError` on a refused call. */
export async function lintPolicy(
  encoded: string,
  snapshot: Readonly<Record<string, unknown>>,
  token: string | null,
): Promise<PolicyLintResult> {
  return apiFetch<PolicyLintResult>(`${repoApiBase(encoded)}/policy/validate`, token, {
    method: 'POST',
    body: JSON.stringify({ yml: JSON.stringify(snapshot) }),
  });
}

/**
 * What the panel shows. `failed` is the CALL failing (403 / outage) — a
 * different thing from the policy failing, and conflating the two would tell a
 * maintainer their file is broken when their session is.
 */
export type PolicyLintState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  | { readonly kind: 'ok' }
  | { readonly kind: 'errors'; readonly errors: readonly PolicyLintError[] }
  | { readonly kind: 'failed'; readonly reason: string };

export type PolicyLintController = {
  readonly state: () => PolicyLintState;
  /** Re-check a snapshot. A second call mid-flight is dropped. */
  readonly run: (snapshot: Readonly<Record<string, unknown>>) => void;
};

/**
 * Lint state for one surface — the verdict is rendered INLINE, never toasted: a
 * list of refusals is something to read and act on, and a notice that fades
 * itself out after 3.5s is not.
 */
export function createPolicyLint(input: {
  readonly encoded: () => string;
  readonly token: string | null;
}): PolicyLintController {
  const [state, setState] = createSignal<PolicyLintState>({ kind: 'idle' });

  const check = async (snapshot: Readonly<Record<string, unknown>>): Promise<void> => {
    setState({ kind: 'running' });
    try {
      const result = await lintPolicy(input.encoded(), snapshot, input.token);
      const errors = result.errors ?? [];
      // A refusal with no detail is not "0 problems found" — that reads as a
      // pass. Report it as a check that couldn't answer.
      setState(
        result.ok
          ? { kind: 'ok' }
          : errors.length > 0
            ? { kind: 'errors', errors }
            : { kind: 'failed', reason: t('repo_detail.policy_lint_failed') },
      );
    } catch (e) {
      setState({ kind: 'failed', reason: errorReason(e, t('repo_detail.policy_lint_failed')) });
    }
  };

  return {
    state,
    run: (snapshot) => {
      if (state().kind !== 'running') void check(snapshot);
    },
  };
}
